/**
 * SITE CONSTRAINTS — THE DETAIL PANEL BODY (design hand-off "Site checks",
 * 15/10/2026, §2 "Detail panel"; boards Drawer + DetailMobile). PURE: snapshot
 * in, escaped HTML out, so it is tested in node and any surface can open it.
 *
 * Rules this file keeps:
 *   - Only fields the snapshot actually has. The board also draws GeoSure
 *     shrink–swell, surface water, reservoir and mine entries; the Phase-1
 *     data has none of them, so those rows say "Not in this dataset" in
 *     --ink-3 — never an invented value.
 *   - Geology ALWAYS shows Bedrock / Superficial / Made ground / Landslip.
 *   - Attribution is visible small text, never a tooltip.
 *   - Every value from the snapshot goes through escapeHtml; every link
 *     through safeUrl.
 */
import { escapeHtml, safeUrl } from '../ui.js';
import {
  severityMark, severityWord, locationBadge, tierOf, sourceSeverity, sourceIcon, meaningLine, officialUrl,
  diffText, fmtDate, toMs, SOURCE_ORDER, SOURCE_LABEL, INDICATIVE_NOTE,
} from './site-checks-ui.js';

const esc = (v) => escapeHtml(String(v == null ? '' : v));
const NOT_IN = '<span class="scb-none">Not in this dataset</span>';
const NONE = '<span class="scb-none">None mapped</span>';
const TRIGGER_WORD = { 'address-change': 'Address changed', manual: 'Re-run', pin: 'Pin moved', 'carried-on-won': 'From the enquiry' };
const FLOOD_SUB = { 1: 'Low probability of river or sea flooding', 2: 'Medium probability of river or sea flooding', 3: 'High probability of river or sea flooding' };
const words = (slug) => String(slug || '').replace(/[-_]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : null);
const shortRock = (n) => String(n || '').split(',')[0].trim().replace(/ (Formation|Group|Member|Deposits?)$/i, '');

/** The large headline value and the phrase beside it, per source. Plain text. */
export function detailHeadline(key, r) {
  if (!r) return { value: 'Not checked', sub: '' };
  if (r.status === 'error') return { value: 'Unavailable', sub: 'The service did not respond' };
  if (r.status === 'not-covered') return { value: 'Not checked', sub: 'England only' };
  if (r.status === 'no-data') return { value: 'No data', sub: '' };
  switch (key) {
    case 'radon': return { value: r.class != null ? `Class ${r.class}` : '—', sub: r.percentRange ? `${r.percentRange} of homes at or above the action level` : '' };
    case 'geology': {
      const b = r.bedrock && r.bedrock.present !== false && r.bedrock.name ? shortRock(r.bedrock.name) : '';
      const s = r.superficial && r.superficial.present !== false && r.superficial.name ? shortRock(r.superficial.name) : '';
      return { value: b && s ? `${b} under ${s.toLowerCase()}` : (b || s || 'None mapped'), sub: '' };
    }
    case 'coal': return { value: r.inDevelopmentHighRiskArea ? 'Development High Risk Area' : r.inReportingArea ? 'Coal mining reporting area' : 'Not in a mapped coal area', sub: '' };
    case 'flood': return { value: r.zone != null ? `Zone ${r.zone}` : '—', sub: FLOOD_SUB[r.zone] || '' };
    case 'planning': {
      const first = Object.entries(r.byDataset || {}).find(([, v]) => Array.isArray(v) && v.length);
      return { value: first ? (first[1][0].name || words(first[0])) : 'None mapped on site', sub: '' };
    }
    default: return { value: '—', sub: '' };
  }
}

const kv = (rows) => `<dl class="scb-kv">${rows.filter(Boolean).map(([k, v]) => `<dt>${esc(k)}</dt><dd>${v}</dd>`).join('')}</dl>`;
const val = (v) => (v == null || v === '' ? '<span class="scb-none">—</span>' : esc(v));

function layerHtml(l) {
  if (!l || l.present === false) return l && l.note ? `<span class="scb-none">${esc(l.note === 'layer not offered by the service' ? 'Not offered by the service' : l.note)}</span>` : NONE;
  const name = l.name || l.description || null;
  if (!name) return NONE;
  return `${esc(name)}${l.rockType ? ` — ${esc(String(l.rockType).toLowerCase())}` : ''}${l.lexCode ? ` <span class="scb-code">${esc(l.lexCode)}</span>` : ''}`;
}

function fieldsHtml(key, r) {
  if (!r) return '';
  if (r.status === 'error') return kv([['Reason', val(r.error || 'No response')]]);
  if (r.status !== 'ok') return '';
  switch (key) {
    case 'radon':
      return kv([['Potential class', val(r.class != null ? `${r.class}${r.percentRange ? ` (${r.percentRange})` : ''}` : null)],
        r.caveat ? ['Note', esc(r.caveat)] : null, r.datasetVersion ? ['Dataset', esc(r.datasetVersion)] : null]);
    case 'geology':
      return `<div class="scb-rows">${[['Bedrock', r.bedrock], ['Superficial', r.superficial], ['Made ground', r.artificialGround], ['Landslip', r.massMovement]]
        .map(([k, l]) => `<div class="scb-row"><span class="scb-row-k">${esc(k)}</span><span class="scb-row-v">${layerHtml(l)}</span></div>`).join('')}</div>
        ${kv([['GeoSure shrink–swell', NOT_IN], ['Map scale', '1:50 000']])}`;
    case 'coal':
      return kv([['High Risk Area', val(yesNo(r.inDevelopmentHighRiskArea))], ['Reporting area', val(yesNo(r.inReportingArea))],
        ['Mine entries ≤ 20 m', NOT_IN], ['Probable shallow workings', NOT_IN]]);
    case 'flood':
      return kv([['Flood zone', val(r.zone)], ['Source', val(r.source === 'planning-data' ? 'planning.data.gov.uk' : r.source === 'ea' ? 'Environment Agency' : r.source)],
        ['Surface water', NOT_IN], ['Reservoir', NOT_IN]]);
    case 'planning': {
      const by = r.byDataset || {};
      const entry = (e) => esc([e.name, e.grade ? `Grade ${e.grade}` : null].filter(Boolean).join(' · ') || e.reference || 'Unnamed');
      const hits = Object.entries(by).filter(([, v]) => Array.isArray(v) && v.length);
      const groups = hits.map(([slug, list]) => `<div class="scb-group"><span class="scb-cap">${esc(words(slug))}</span>
        ${list.map((e) => `<div class="scb-row"><span class="scb-row-v">${entry(e)}</span>${e.reference ? `<span class="scb-code">${esc(e.reference)}</span>` : ''}</div>`).join('')}</div>`).join('');
      const misses = ['conservation-area', 'listed-building', 'green-belt'].filter((s) => !(Array.isArray(by[s]) && by[s].length))
        .map((s) => `<div class="scb-row"><span class="scb-row-k">${esc(s === 'listed-building' ? 'Listed building on site' : words(s))}</span><span class="scb-row-v">${NONE}</span></div>`).join('');
      const near = Array.isArray(r.nearbyListed) ? r.nearbyListed : [];
      return `${groups ? `<span class="scb-cap">Designations on site</span>${groups}` : ''}
        ${misses ? `<div class="scb-rows">${misses}</div>` : ''}
        <span class="scb-cap">Nearby listed buildings (within 50 m)</span>
        ${near.length ? `<div class="scb-rows">${near.map((e) => `<div class="scb-row"><span class="scb-row-v">${entry(e)}</span>${e.reference ? `<span class="scb-code">${esc(e.reference)}</span>` : ''}</div>`).join('')}</div>`
          : '<span class="scb-none">None found in public data</span>'}
        <span class="scb-attr">Coverage varies by council; no result does not mean no constraint.</span>`;
    }
    default: return '';
  }
}

function sectionHtml(key, snap) {
  const r = snap?.results?.[key];
  const sev = sourceSeverity(key, snap);
  const h = detailHeadline(key, r);
  const url = officialUrl(key, snap);
  const short = h.value.length <= 8;
  return `<section class="scb-d-sec" id="scb-d-${esc(key)}" data-sc-sec="${esc(key)}">
    <div class="scb-d-head"><span class="scb-d-ico">${sourceIcon(key)}</span><span class="pdx-eyebrow is-link">${esc(SOURCE_LABEL[key])}</span><span class="pdx-flex"></span>
      ${sev === 'none' ? '<span class="site-sev info">No flag</span>' : `<span class="scb-d-sev">${severityMark(sev, { size: 10, label: false })}${severityWord(sev)}</span>`}</div>
    <div class="scb-d-val-row"><span class="scb-d-val${short ? ' is-short' : ''}">${esc(h.value)}</span>${h.sub ? `<span class="scb-d-sub">${esc(h.sub)}</span>` : ''}</div>
    <p class="scb-d-mean">${esc(meaningLine(key, snap))}</p>
    ${fieldsHtml(key, r)}
    <div class="scb-d-foot">${r && r.attribution ? `<span class="scb-attr">${esc(r.attribution)}</span>` : '<span></span>'}
      ${url ? `<a class="scb-d-link" href="${esc(safeUrl(url))}" target="_blank" rel="noopener">View on official map ↗</a>` : ''}</div>
  </section>`;
}

/** The panel subtitle: checked date + location badge (HTML; every value escaped). */
export function detailSubtitleHtml(snap) {
  if (!snap) return '';
  const tier = tierOf(snap);
  const pin = tier === 'pin' && snap.input && snap.input.lat != null ? `${Number(snap.input.lat).toFixed(4)}, ${Number(snap.input.lng).toFixed(4)}` : '';
  return `Checked ${esc(fmtDate(snap.createdAt))} ${locationBadge(tier, { extra: pin })}`;
}

/** The body: tab strip, the blue note, the five sections, previous checks. */
export function siteDetailHtml(snap, { history = [], canPin = true } = {}) {
  if (!snap) return '<p class="g-note">No site check on record yet.</p>';
  const earlier = (Array.isArray(history) ? history : []).filter((h) => h && (h.id ? h.id !== snap.id : toMs(h.createdAt) !== toMs(snap.createdAt)));
  const tier = tierOf(snap);
  const rows = [snap, ...earlier].map((h, i) => {
    const d = diffText(h);
    const what = i === 0 ? (d ? `Current · ${d}` : 'Current') : (d || TRIGGER_WORD[h.trigger] || 'Earlier check');
    return `<div class="scb-hist"><span class="scb-code">${esc(fmtDate(h.createdAt))}</span>${locationBadge(tierOf(h))}<span class="scb-hist-what">${esc(what)}</span></div>`;
  }).join('');
  return `<nav class="scb-tabs" aria-label="Sources">${SOURCE_ORDER.map((k, i) => `<button type="button" class="scb-tab${i === 0 ? ' active' : ''}" data-sc-tab="${esc(k)}"${i === 0 ? ' aria-current="true"' : ''}>${esc(SOURCE_LABEL[k])}</button>`).join('')}</nav>
    ${tier === 'postcode' && canPin ? `<button type="button" class="scb-d-apx" data-sc="pin">${severityMark('info', { size: 12, label: false })}<span>Results use the postcode centre. <b>Drop a pin</b></span></button>` : ''}
    <div class="scb-d-note">${esc(INDICATIVE_NOTE)}</div>
    ${SOURCE_ORDER.map((k) => sectionHtml(k, snap)).join('')}
    <section class="scb-d-sec scb-d-prev"><span class="pdx-eyebrow is-link">Previous checks</span><div class="scb-hist-list">${rows}</div></section>`;
}
